import React, { useState, useEffect, useCallback } from 'react';

const containerStyle = (position: 'left' | 'right'): React.CSSProperties => ({
  position: 'fixed',
  bottom: position === 'right' ? '40px' : '160px',
  left: position === 'right' ? 'auto' : '20px',
  right: position === 'right' ? '40px' : 'auto',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const buttonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #e63946 0%, #d62839 100%)',
  color: 'white',
  border: 'none',
  borderRadius: '50px',
  padding: '14px 24px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 4px 15px rgba(230, 57, 70, 0.4)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: '32px',
  height: '32px',
  fontSize: '14px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const iosInstructionsStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '16px',
  padding: '20px 24px',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
  textAlign: 'center',
  maxWidth: '280px',
};

const iosButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: 'linear-gradient(135deg, #007aff 0%, #0051d5 100%)',
  boxShadow: '0 4px 15px rgba(0, 122, 255, 0.4)',
};

const dismissButtonStyle: React.CSSProperties = {
  background: '#007aff',
  color: 'white',
  border: 'none',
  borderRadius: '25px',
  padding: '10px 24px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: '12px',
};

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPWAButtonProps {
  className?: string;
  position?: 'left' | 'right';
}

const InstallPWAButton: React.FC<InstallPWAButtonProps> = ({ className = '', position = 'left' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [wasDismissed, setWasDismissed] = useState(false);

  // Detect if running as installed PWA
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator as any).standalone === true;
    setIsInstalled(isStandalone);
  }, []);

  // Check if user previously dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const daysSinceDismissed = Math.floor((now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24));
      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        setWasDismissed(true);
      }
    }
  }, []);

  // Detect iOS
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIOSDevice);
  }, []);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Also listen for appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      localStorage.removeItem('pwa-install-dismissed');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();

    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
      localStorage.removeItem('pwa-install-dismissed');
    } else {
      // Se cancelou, recarrega para que o evento beforeinstallprompt possa disparar novamente
      window.location.reload();
    }

    setDeferredPrompt(null);
    setIsInstallable(false);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
    setWasDismissed(true);
  }, []);

  // Don't show if already installed, dismissed, or not installable
  if (isInstalled || wasDismissed) {
    return null;
  }

  // iOS Instructions
  if (isIOS) {
    return (
      <div style={containerStyle(position)} className={className}>
        {showIOSInstructions ? (
          <div style={iosInstructionsStyle}>
            <meta name="theme-color" content="#000000" />
            <p>
              <span role="img" aria-label="share">📤</span> Toque no botão <strong>Compartilhar</strong>
            </p>
            <p>
              <span role="img" aria-label="add">➕</span> Depois em <strong>Adicionar à Tela de Início</strong>
            </p>
            <button 
              onClick={() => setShowIOSInstructions(false)}
              style={dismissButtonStyle}
              type="button"
            >
              Entendi
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={() => setShowIOSInstructions(true)}
              style={buttonStyle}
              type="button"
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(230, 57, 70, 0.6)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(230, 57, 70, 0.4)';
              }}
            >
              <span role="img" aria-label="phone">📲</span> Instalar App
            </button>
            <button
              onClick={handleDismiss}
              style={closeButtonStyle}
              type="button"
              aria-label="Fechar"
            >
              ✕
            </button>
          </>
        )}
      </div>
    );
  }

  // Android/Chrome - show button even if not installable (for dashboard)
  return (
    <div style={containerStyle(position)} className={className}>
      <button
        onClick={handleInstallClick}
        style={buttonStyle}
        type="button"
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(230, 57, 70, 0.6)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(230, 57, 70, 0.4)';
        }}
      >
        <span role="img" aria-label="download">📲</span> Instalar App
      </button>
      <button
        onClick={handleDismiss}
        style={closeButtonStyle}
        type="button"
        aria-label="Fechar"
      >
        ✕
      </button>
    </div>
  );
};

export default InstallPWAButton;
